import { setWindowLocation } from './browser';

export * from './animation';
export * from './browser';
export * from './dom';
export * from './event';
export * from './filter-tree';
export * from './format';
export { deepClone, isEqual, sortByKey } from './object';
export * from './object';
export * from './params';
export * from './params';
export * from './recentlyViewedProduct';
export * from './settings';
export * from './translation';
export * from './validate';

/**
 * Safely parses a JSON string, catching and logging any errors.
 * Returns the parsed JSON object, or null if parsing fails.
 * @type {import('./index.d').safeParseJSON}
 */

function preprocessInchesQuotesLogic(str) {
  if (!str) return str;

  // Match numbers with optional feet (') and inches before a double quote.
  const inch_quote = /(\d+'?\s*\d*)"(?!\s*:)(?=\s*(x|\/|"|$))/g;

  // Only escape the double quote, keeping the numeric/feet/inches part intact
  return str.replace(inch_quote, (match) => match.slice(0, -1) + '\\"');
}

export const safeParseJSON = (jsonString) => {
  if (typeof jsonString !== 'string') return null;
  try {
    return JSON.parse(jsonString);
  } catch (e1) {
    const fixed = preprocessInchesQuotesLogic(jsonString);
    try {
      return JSON.parse(fixed);
    } catch (e2) {
      console.error(
        'JSON parsing failed after inch-quote preprocessing.',
        '\nOriginal error:',
        e1,
        '\nSecond error:',
        e2,
        '\nPreprocessed string:',
        fixed
      );
      return null;
    }
  }
};

/**
 * Generates an MD5 hash for the given input string.
 *
 * Uses bitwise operations and rotations to hash the
 * input string one character at a time.
 * @type {import('./index.d').simpleMd5}
 */
export function simpleMd5(input) {
  const inputLength = input.length;

  let a = 0x67452301;
  let b = 0xefcdab89;
  let c = 0x98badcfe;
  let d = 0x10325476;

  for (let i = 0; i < inputLength; i++) {
    a = a ^ input.charCodeAt(i);

    a = (a >>> 7) | (a << (32 - 7));

    c = (c + a + b) & 0xffffffff;
    b = (b >>> 17) | (b << (32 - 17));
    d = (d + c + a) & 0xffffffff;
    a = (a >>> 19) | (a << (32 - 19));

    b = b ^ input.charCodeAt(i);

    b = (b >>> 10) | (b << (32 - 10));

    c = (c + b + d) & 0xffffffff;
    d = (d >>> 22) | (d << (32 - 22));
    a = (a + c + b) & 0xffffffff;
    b = (b >>> 23) | (b << (32 - 23));
  }

  let md5Result =
    'w-' +
    [a, b, c, d]
      .map(function (i) {
        return i.toString(16).padStart(2, '0');
      })
      .join('');
  if (md5Result.startsWith('-')) {
    md5Result = md5Result.slice(1);
  }

  return md5Result;
}

/**
 * Scrolls the window to the top.
 * @param behavior "auto" | "smooth"
 * @param isFilter true | false
 * boostSdDisableScrollToTop - disabled scroll to top after Filter
 * window.boostSdScrollTopSticky - sticky menu and toolbar ...
 *
 * @type {import('./index.d').scrollToTop}
 */
export function scrollToTop({ behavior = 'smooth', isFilter = false } = {}) {
  let top = 0;

  if (
    window.boostSdDisableScrollToTop ||
    (window.boostSdDisableScrollToTopAfterFiltered && isFilter)
  ) {
    return;
  }

  if (isFilter) {
    const layout = document.querySelector('.boost-sd-right');
    if (!layout) return;

    const layoutTop = layout.getBoundingClientRect()?.top;
    const _top = layoutTop + window.scrollY - (window.boostSdScrollTopSticky || 150);

    if (window.scrollY > layoutTop && _top > 0) {
      top = _top;
    }
  }

  window.scrollTo({
    top: top,
    behavior,
  });
}

/**
 * Waits for the given number of milliseconds before resolving the promise.
 * Useful for delaying or throttling code execution.
 *
 * @param {number} ms - Number of milliseconds to wait before resolving the promise.
 * @type {import('./index.d').waitFor}
 */
export const waitFor = (ms) => {
  return new Promise((resolve) => {
    setTimeout(() => resolve(), ms);
  });
};

/**
 * @type {import('./index.d').stripHtml}
 */
export const stripHtml = (string) => {
  const strippedHtml = string?.toString()?.replace(/<.*?>/gi, '') || '';
  const parser = new DOMParser();
  const parsedDoc = parser.parseFromString(strippedHtml, 'text/html');
  return parsedDoc.documentElement.textContent || '';
};

/**
 * @type {import('./index.d').buildUrlWithLocalization}
 */
export const buildUrlWithLocalization = (url) => {
  if (!url) return '/';

  const routesRoot = window?.Shopify?.routes?.root || '/';

  return `${routesRoot}${url}`;
};

/**
 * @type {import('./index.d').loadResource}
 */
export const loadResource = (script, position = 'body', keySource = 'src') => {
  return new Promise((resolve, reject) => {
    script.onload = function () {
      resolve(true);
    };

    script.onerror = function (error) {
      reject(error);
    };

    switch (position) {
      case 'head': {
        document.head.appendChild(script);
        break;
      }

      case 'body': {
        document.body.appendChild(script);
        break;
      }
    }
  });
};

/**
 * @type {import('./index.d').slugify}
 */
export const slugify = (text) => {
  return text
    .toString() // Cast to string (optional)
    .normalize('NFKD') // The normalize() using NFKD method returns the Unicode Normalization Form of a given string.
    .toLowerCase() // Convert the string to lowercase letters
    .trim()
    .replace(/[\s/]+/g, '-') // Replace spaces with -
    .replace(/[`~!@#$%^&*()|+\-=?;:'",.<>{}[\]\\/]/g, '-') // Replace all special characters with -
    .replace(/--+/g, '-') // Replace multiple - with single -
    .replace(/^-+/, '') // Trim - from start of text
    .replace(/-+$/, ''); // Trim - from end of text
};

/**
 * Get file path
 * @type {import('./index.d').getFilePath}
 */
export const getFilePath = (fileName, extensionArg, versionArg) => {
  const extension = typeof extensionArg !== 'undefined' ? extensionArg : 'png';
  const version = typeof versionArg !== 'undefined' ? versionArg : '';
  const defaultFileUrl = '//cdn.shopify.com/s/files/1/0663/1463/3470/files/?10045';

  let filePath = defaultFileUrl.split('?')[0];
  filePath += fileName + '.' + extension + (version ? '?v=' + version : '');

  return filePath;
};

/**
 * @type {import('./index.d').getProductMetafield}
 */
export const getProductMetafield = (productData, namespace, key) => {
  const metafieldValue = productData?.metafields?.filter(function (item) {
    return item?.namespace === namespace && item.key === key;
  });

  return metafieldValue?.[0]?.value ?? '';
};

/**
 * @type {import('./index.d').getTenantId}
 */
export const getTenantId = () => {
  let tenantId = window.Shopify?.shop;
  const env = window.boostWidgetIntegration.env || 'production';
  const shopDomain = window.boostWidgetIntegration?.app?.[env]?.shop?.domain;
  if (window.boostWidgetIntegration && !tenantId && shopDomain) {
    tenantId = shopDomain;
  }

  return tenantId;
};

/**
 * @type {import('./index.d').handleClickTagAItemBuildUrlWithLocale}
 */
export const handleClickTagAItemBuildUrlWithLocale = (event, classesProduct) => {
  let productLink = event?.target?.closest(classesProduct)?.getAttribute('href');
  if (productLink) {
    event.preventDefault();

    const routesRoot = window?.Shopify?.routes?.root || '/';

    // other redirects -> none build locale or product link have locale
    if (productLink?.startsWith('https://') || productLink.startsWith(routesRoot)) {
      setWindowLocation(productLink);
    } else {
      // search link
      if (productLink[0] === '/') {
        // remove / first
        productLink = productLink.replace('/', '');
      }
      const productLinkWithLocale = buildUrlWithLocalization(productLink);
      setWindowLocation(productLinkWithLocale);
    }
  }
};

/**
 * @type {import('./index.d').isElementInViewport}
 */
export const isElementInViewport = (el) => {
  if (!el) return;

  const rect = el?.getBoundingClientRect() || {
    top: 0,
    left: 0,
    bottom: 0,
    right: 0,
  };

  return (
    rect.top >= 0 &&
    rect.left >= 0 &&
    rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
    rect.right <= (window.innerWidth || document.documentElement.clientWidth)
  );
};

/**
 * @type {import('./index.d').handleFirstLoadAfterRender}
 */
export const handleFirstLoadAfterRender = (context) => {
  if (typeof context.app?.customization[context.blockType]?.afterRender === 'function') {
    context.app.customization[context.blockType].afterRender();
  }
  if (typeof context.app?.customization?.afterRender === 'function') {
    context.app.customization.afterRender();
  }
};
/**
 * @type {import('./index.d').getCustomerId}
 */
export function getCustomerId() {
  // https://community.shopify.com/c/shopify-apis-and-sdks/current-best-way-to-get-logged-in-customer-id/td-p/482773
  return (
    window?.__st?.cid ||
    window?.meta?.page?.customerId ||
    window?.ShopifyAnalytics?.meta?.page?.customerId ||
    window?.ShopifyAnalytics?.lib?.user?.()?.traits?.()?.uniqToken
  );
}

/**
 * Returns the urlScheme.
 * @type {import('./index.d').getUrlScheme}
 */
export const getUrlScheme = () => {
  // urlScheme - 0: dont change url, 1: example 'color=red&color=blue', 2: example 'color=red,blue'
  return window?.boostWidgetIntegrationConfig?.filter?.urlScheme || 2;
};
